"use client";

import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getLegalLinks } from "@/constants/legalLinks";
import { todayAsIsoDate } from "@/utils/date";
import { ageInYearsAt } from "@/utils/eventAgeGroup";
import { Eye, EyeOff } from "lucide-react";
import { useTranslations } from "next-intl";
import { type FormEvent, useId, useState } from "react";
import { z } from "zod";

const emailSchema = z.email();

interface CreateAccountStepProps {
  eventId: string;
  locale: string;
  /** Account created (unverified) — advance to confirm-email with the email. */
  onCreated: (email: string) => void;
}

interface FieldErrors {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

function PasswordField({
  id,
  label,
  value,
  onChange,
  error,
  autoComplete,
  showLabel,
  hideLabel,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
  autoComplete: string;
  showLabel: string;
  hideLabel: string;
}) {
  const [visible, setVisible] = useState(false);
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <div className="relative">
        <Input
          id={id}
          type={visible ? "text" : "password"}
          autoComplete={autoComplete}
          value={value}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(e) => onChange(e.target.value)}
          className="pr-10"
        />
        <button
          type="button"
          aria-label={visible ? hideLabel : showLabel}
          onClick={() => setVisible((v) => !v)}
          className="absolute inset-y-0 right-3 flex items-center text-muted-foreground"
        >
          {visible ? (
            <EyeOff className="h-5 w-5" aria-hidden />
          ) : (
            <Eye className="h-5 w-5" aria-hidden />
          )}
        </button>
      </div>
      {error && (
        <p id={`${id}-error`} className="text-sm text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

/**
 * Create-account step (design `14510:4863`). Collects login credentials +
 * optional marketing consent and posts to the server-side create proxy. The
 * account is created unverified (no session) → advances to confirm-email. An
 * already-registered email does NOT error here: Spond Core re-sends a
 * verification code (anti-enumeration by design), so the user proceeds through
 * confirm-email exactly as a new registrant would.
 */
export default function CreateAccountStep({
  eventId,
  locale,
  onCreated,
}: CreateAccountStepProps) {
  const t = useTranslations("publicEventSignup");
  const basePath = process.env.NEXT_PUBLIC_BASE_PATH ?? "";
  const legalLinks = getLegalLinks(locale);
  const productsLabelId = useId();
  const partnersLabelId = useId();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [dateOfBirth, setDateOfBirth] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [intMarketing, setIntMarketing] = useState(false);
  const [extMarketing, setExtMarketing] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  function validate(): FieldErrors {
    const errors: FieldErrors = {};
    if (firstName.trim().length === 0)
      errors.firstName = t("firstNameRequired");
    if (lastName.trim().length === 0) errors.lastName = t("lastNameRequired");
    // Required, but no minimum age here: this is an account, not an event
    // signup — the 18+ rule belongs to the event that asks for it.
    if (!dateOfBirth) errors.dateOfBirth = t("dobRequired");
    else if (ageInYearsAt(dateOfBirth) === null)
      errors.dateOfBirth = t("dobInvalid");
    if (!emailSchema.safeParse(email).success) errors.email = t("emailInvalid");
    if (password.length < 8) errors.password = t("passwordTooShort");
    // confirmPassword is matched client-side only and never sent to the server.
    if (confirmPassword !== password) {
      errors.confirmPassword = t("passwordsDoNotMatch");
    }
    return errors;
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) return;

    setSubmitting(true);
    try {
      const res = await fetch(
        `${basePath}/api/events/${eventId}/signup/create`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: firstName.trim(),
            lastName: lastName.trim(),
            dateOfBirth,
            email,
            password,
            intMarketing,
            extMarketing,
            locale,
          }),
        },
      );
      const data = (await res.json().catch(() => ({}))) as {
        status?: string;
        error?: string;
      };

      if (res.ok && data.status === "created") {
        onCreated(email);
        return;
      }
      // Prefer the backend's localized 4xx message (e.g. password policy); fall
      // back to generic copy when the proxy didn't forward one. A 409 (the rare
      // concurrent-insert race) deliberately carries no body, so it lands here
      // as generic copy rather than revealing that the address exists.
      setFormError(data.error ?? t("createAccountError"));
    } catch {
      setFormError(t("createAccountError"));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-lg font-semibold">{t("createAccount")}</h2>
        <p className="text-sm text-muted-foreground">
          {t("createAccountDescription")}
        </p>
      </div>

      <form
        onSubmit={handleSubmit}
        className="flex flex-1 flex-col gap-4"
        noValidate
      >
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-first-name">{t("firstNameLabel")}</Label>
          <Input
            id="signup-first-name"
            autoComplete="given-name"
            value={firstName}
            aria-invalid={!!fieldErrors.firstName}
            aria-describedby={
              fieldErrors.firstName ? "signup-first-name-error" : undefined
            }
            onChange={(e) => setFirstName(e.target.value)}
          />
          {fieldErrors.firstName && (
            <p
              id="signup-first-name-error"
              className="text-sm text-destructive"
            >
              {fieldErrors.firstName}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-last-name">{t("lastNameLabel")}</Label>
          <Input
            id="signup-last-name"
            autoComplete="family-name"
            value={lastName}
            aria-invalid={!!fieldErrors.lastName}
            aria-describedby={
              fieldErrors.lastName ? "signup-last-name-error" : undefined
            }
            onChange={(e) => setLastName(e.target.value)}
          />
          {fieldErrors.lastName && (
            <p id="signup-last-name-error" className="text-sm text-destructive">
              {fieldErrors.lastName}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-dob">{t("dobLabel")}</Label>
          <Input
            id="signup-dob"
            type="date"
            className="appearance-none"
            autoComplete="bday"
            max={todayAsIsoDate()}
            value={dateOfBirth}
            aria-invalid={!!fieldErrors.dateOfBirth}
            aria-describedby={
              fieldErrors.dateOfBirth ? "signup-dob-error" : undefined
            }
            onChange={(e) => setDateOfBirth(e.target.value)}
          />
          {fieldErrors.dateOfBirth && (
            <p id="signup-dob-error" className="text-sm text-destructive">
              {fieldErrors.dateOfBirth}
            </p>
          )}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="signup-email">{t("emailLabel")}</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            value={email}
            aria-invalid={!!fieldErrors.email}
            aria-describedby={
              fieldErrors.email ? "signup-email-error" : undefined
            }
            onChange={(e) => setEmail(e.target.value)}
          />
          {fieldErrors.email && (
            <p id="signup-email-error" className="text-sm text-destructive">
              {fieldErrors.email}
            </p>
          )}
        </div>

        <PasswordField
          id="signup-password"
          label={t("passwordLabel")}
          value={password}
          onChange={setPassword}
          error={fieldErrors.password}
          autoComplete="new-password"
          showLabel={t("showPassword")}
          hideLabel={t("hidePassword")}
        />

        <PasswordField
          id="signup-confirm-password"
          label={t("confirmPasswordLabel")}
          value={confirmPassword}
          onChange={setConfirmPassword}
          error={fieldErrors.confirmPassword}
          autoComplete="new-password"
          showLabel={`${t("showPassword")} – ${t("confirmPasswordLabel")}`}
          hideLabel={`${t("hidePassword")} – ${t("confirmPasswordLabel")}`}
        />

        {/* Checkbox is labelled via aria-labelledby; not a native <label>. */}
        <div className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={intMarketing}
            aria-labelledby={productsLabelId}
            onCheckedChange={(c) => setIntMarketing(c === true)}
          />
          <span id={productsLabelId}>{t("marketingProducts")}</span>
        </div>

        <div className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={extMarketing}
            aria-labelledby={partnersLabelId}
            onCheckedChange={(c) => setExtMarketing(c === true)}
          />
          <span id={partnersLabelId}>{t("marketingPartners")}</span>
        </div>

        {formError && (
          <p className="text-sm text-destructive" role="alert">
            {formError}
          </p>
        )}

        <Button
          type="submit"
          variant="default"
          disabled={submitting}
          className="mt-auto w-full rounded-full"
        >
          {t("continue")}
        </Button>
      </form>

      {/* Implicit acceptance, matching spond.com/landing/register — no checkbox. */}
      <p className="text-center text-xs text-muted-foreground">
        {t.rich("policyLinks", {
          termsLink: (chunks) => (
            <a
              href={legalLinks.terms}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {chunks}
            </a>
          ),
          privacyLink: (chunks) => (
            <a
              href={legalLinks.privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="underline hover:text-foreground"
            >
              {chunks}
            </a>
          ),
        })}
      </p>
    </div>
  );
}
